import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../services/api";
import { normalizeSearchText } from "../utils/filterUtils";
import TestInterface from "./TestInterface";
import ShineLogo from "./ShineLogo";
import StudentClasses from "./StudentClasses";
import { CandidateAnnouncements, CandidateBookmarks, CandidateDocuments, CandidateBookmark } from "./CandidateResources";
import ValueHelpField from "./ValueHelpField";
import { ParsedQuestionPreview } from "./ParsedQuestionPreview";
import DynamicWatermark from "../security/DynamicWatermark";
import { useTenant } from "../context/TenantContext";
import StudentTestSeriesView from "./TestSeries/StudentTestSeriesView";
import CurrentAffairsHub from "./CurrentAffairs/CurrentAffairsHub";
import PerformanceHeatmap from "./Analytics/PerformanceHeatmap";
import "./AnswererDashboard.css";
import "./CandidateValidity.css";

type PortalView = "tests" | "test-series" | "current-affairs" | "performance" | "report" | "classes" | "bookmarks" | "documents" | "announcements";
type TestTab = "active" | "upcoming" | "missed" | "completed";
type ReportTab = "score" | "subject" | "solution" | "questions" | "compare";

interface Props { userName: string; onLogout: () => void }
interface AssignedTest {
  id: string; name: string; duration: number; questions: number;
  status: "active" | "draft" | "completed" | "upcoming" | "expired"; totalMarks?: number;
  passingPercentage?: number; attempted?: boolean;
  availableFrom?: string; validUntil?: string;
  categoryId?: string; categoryName?: string; subcategoryId?: string; subcategoryName?: string; stage?: string;
  attemptStatus?: "not_started" | "in_progress" | "submitted";
  attemptId?: string;
  answeredCount?: number;
  totalQuestions?: number;
  timeSpentSec?: number;
  currentQuestionIndex?: number;
  currentSection?: string;
  lastSavedAt?: string;
}

interface ExamForTaking {
  id: string; testName: string; duration: number;
  passingPercentage?: number; questions: any[];
  timerMode?: "overall" | "sectional";
  sectionConfig?: Array<{ name: string; duration: number; questionCount: number; marks: number }>;
  negativeMarkingScheme?: string;
  negativeMarks?: number;
  isSeries?: boolean;
  seriesId?: string;
  paperId?: string;
  customInstructions?: string;
}
interface TestHistoryItem {
  attemptId: string; examId: string; testName: string; submittedAt: string;
  scoredMarks: number; totalMarks: number; percentage: number; passed: boolean; timeSpentSec: number;
}
interface ResultDetail {
  sectionWise?: Record<string, { total: number; scored: number }>;
  questionReview?: Array<{ questionId: string; question?: string; type?: string; options?: string[]; isCorrect: boolean; userAnswer: string | string[]; correctAnswer?: string | string[]; marks: number; section: string; timeSpentSec?:number;avgTimeSec?:number;topperTimeSec?:number;topperUserId?:string }>;
}
interface AccountInfo { userId: string; name?: string; email?: string; collegeEmail?: string; isActive?: boolean; lastLoginAt?: string; validUntil?: string }
interface PortalNotification { id: string; type: "test" | "result" | "document" | "announcement"; title: string; message: string; target: PortalView; createdAt: string; read: boolean }
interface ExamPage {
  groupSlug: string; examSlug: string; stageSlug: string;
  groupLabel: string; examLabel: string; stageLabel: string;
  categoryId: string; subcategoryId: string; stage: string;
}
interface CategoryData { id:string;name:string;slug:string;subcategories:Array<{id:string;name:string;slug:string;stages:string[]}> }

const navItems: Array<{ view: PortalView; label: string; icon: React.ReactNode }> = [
  {
    view: "tests",
    label: "My Tests",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
  },
  {
    view: "classes",
    label: "Classes",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    ),
  },
  {
    view: "report",
    label: "Reports",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    view: "bookmarks",
    label: "Bookmarks",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
  },
  {
    view: "documents",
    label: "Documents",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    view: "test-series",
    label: "Test Series",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    ),
  },
  {
    view: "current-affairs",
    label: "Current Affairs",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
        <path d="M18 14h-8" />
        <path d="M15 18h-5" />
        <path d="M10 6h8v4h-8V6Z" />
      </svg>
    ),
  },
  {
    view: "performance",
    label: "Performance",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    view: "announcements",
    label: "Announcements",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
];

const AnswererDashboard: React.FC<Props> = ({ userName, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tests, setTests] = useState<AssignedTest[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [history, setHistory] = useState<TestHistoryItem[]>([]);
  const [activeExam, setActiveExam] = useState<ExamForTaking | null>(null);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState("");
  const [testTab, setTestTab] = useState<TestTab>("active");
  const [reportTab, setReportTab] = useState<ReportTab>("score");
  const [gridView, setGridView] = useState(() => localStorage.getItem("shine_default_view") !== "list");
  const [search, setSearch] = useState("");
  const [reportSearch, setReportSearch] = useState("");
  const [selectedReport, setSelectedReport] = useState<TestHistoryItem | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [reportDetail, setReportDetail] = useState<ResultDetail | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["banking"]);
  const [expandedExams, setExpandedExams] = useState<string[]>([]);
  const [testsTreeOpen, setTestsTreeOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [bookmarks, setBookmarks] = useState<CandidateBookmark[]>([]);
  const [accountPanel, setAccountPanel] = useState<"profile" | "settings" | null>(null);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [emailNotifications, setEmailNotifications] = useState(() => localStorage.getItem("shine_email_notifications") !== "off");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const { loadTenantBranding, tenant } = useTenant();

  useEffect(() => {
    const tid = sessionStorage.getItem("activeTenantId") || sessionStorage.getItem("tenantId");
    if (tid) {
      loadTenantBranding(tid);
    }
  }, []);

  const view: PortalView = location.pathname.includes("classes") || location.pathname.includes("videos")
    ? "classes" : location.pathname.includes("history") || location.pathname.includes("report")
    ? "report" : location.pathname.includes("bookmark") ? "bookmarks"
    : location.pathname.includes("document") || location.pathname.includes("courses") ? "documents"
    : location.pathname.includes("announcement") ? "announcements"
    : location.pathname.includes("test-series") ? "test-series"
    : location.pathname.includes("current-affairs") ? "current-affairs"
    : location.pathname.includes("performance") || location.pathname.includes("readiness") ? "performance"
    : "tests";
  const examGroups=useMemo(()=>categoryData.map(category=>({id:category.id,slug:category.slug,label:category.name,exams:category.subcategories.map(sub=>({id:sub.id,slug:sub.slug,label:sub.name,stages:sub.stages}))})),[categoryData]);
  const examPages=useMemo<ExamPage[]>(()=>examGroups.flatMap(group=>group.exams.flatMap(exam=>exam.stages.map(stage=>({groupSlug:group.slug,examSlug:exam.slug,stageSlug:stage.toLowerCase().replace(/[^a-z0-9]+/g,"-"),groupLabel:group.label,examLabel:exam.label,stageLabel:stage,categoryId:group.id,subcategoryId:exam.id,stage})))),[examGroups]);
  const selectedExamPage = examPages.find(page => location.pathname === `/dashboard/exams/${page.groupSlug}/${page.examSlug}/${page.stageSlug}`);

  const loadPortal = async () => {
    setLoading(true);
    try {
      const [testRes, historyRes] = await Promise.allSettled([
        apiGet<{ tests: AssignedTest[] }>(`/answerer/tests?userId=${encodeURIComponent(userName)}`),
        apiGet<{ history: TestHistoryItem[] }>(`/answerer/history?userId=${encodeURIComponent(userName)}`),
      ]);
      const loadedTests = testRes.status === "fulfilled" ? (testRes.value?.tests || []) : [];
      const loadedHistory = historyRes.status === "fulfilled" ? (historyRes.value?.history || []) : [];
      setTests(loadedTests);
      setHistory(loadedHistory);
      apiGet<{categories:CategoryData[]}>(`/answerer/exam-categories?userId=${encodeURIComponent(userName)}`).then(res=>setCategoryData(res.categories||[])).catch(()=>setCategoryData([]));
      apiGet<{ notifications: PortalNotification[] }>(`/answerer/notifications?userId=${encodeURIComponent(userName)}`)
        .then(res => setNotifications(res.notifications || [])).catch(() => setNotifications([]));
      apiGet<{ bookmarks: CandidateBookmark[] }>(`/answerer/bookmarks?userId=${encodeURIComponent(userName)}`)
        .then(res => setBookmarks(res.bookmarks || [])).catch(() => setBookmarks([]));
      if (!selectedReport && loadedHistory.length) setSelectedReport(loadedHistory[0]);
    } catch (error) {
      console.error(error);
    } finally { setLoading(false); }
  };

  const loadBookmarks = () => apiGet<{ bookmarks: CandidateBookmark[] }>(`/answerer/bookmarks?userId=${encodeURIComponent(userName)}`)
    .then(res => setBookmarks(res.bookmarks || [])).catch(() => setBookmarks([]));
  const toggleTestBookmark = async (test: AssignedTest) => {
    await apiPost("/answerer/bookmarks/toggle", { userId: userName, type: "test", testId: test.id, title: test.name });
    await loadBookmarks();
  };
  const openBookmarkedTest = (testId: string) => {
    const test = tests.find(item => item.id === testId);
    if (test && categoryFor(test) === "active") void startExam(testId);
    else { setTestTab(test ? categoryFor(test) : "active"); goTo("tests"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPortal(); }, [userName]);
  // When user switches to Reports view, reload history if it's empty
  useEffect(() => {
    if (view === "report" && history.length === 0) {
      apiGet<{ history: TestHistoryItem[] }>(`/answerer/history?userId=${encodeURIComponent(userName)}`)
        .then(res => {
          const h = res.history || [];
          setHistory(h);
          if (h.length && !selectedReport) setSelectedReport(h[0]);
        }).catch(() => {});
    }
  }, [view, userName]);
  useEffect(() => {
    if (history.length && !selectedReport) setSelectedReport(history[0]);
  }, [history]);
  useEffect(() => {
    if (!selectedReport?.attemptId) { setReportDetail(null); return; }
    apiGet<{ result: ResultDetail }>(`/answerer/results/${selectedReport.attemptId}`)
      .then(res => setReportDetail(res.result)).catch(() => setReportDetail(null));
  }, [selectedReport]);

  const goTo = (next: PortalView) => {
    const path = next === "tests" ? "/dashboard" : `/dashboard/${next}`;
    navigate(path);
    setProfileOpen(false);
  };
  const unreadNotifications = notifications.filter(item => !item.read).length;
  const markNotificationRead = async (notificationId: string) => {
    setNotifications(items => items.map(item => notificationId === "all" || item.id === notificationId ? { ...item, read: true } : item));
    try { await apiPost("/answerer/notifications/read", { userId: userName, notificationId }); } catch (error) { console.error(error); }
  };
  const clearNotifications = async (notificationId: string = "all") => {
    setNotifications(items => notificationId === "all" ? [] : items.filter(item => item.id !== notificationId));
    try { await apiPost("/answerer/notifications/clear", { userId: userName, notificationId }); } catch (error) { console.error(error); }
  };

  const openExamPage = (page: ExamPage) => {
    setTestTab("active");
    setSearch("");
    navigate(`/dashboard/exams/${page.groupSlug}/${page.examSlug}/${page.stageSlug}`);
  };
  const toggleExpanded = (value: string, values: string[], setValues: React.Dispatch<React.SetStateAction<string[]>>) =>
    setValues(values.includes(value) ? values.filter(item => item !== value) : [...values, value]);

  const openAccountPanel = (panel: "profile" | "settings") => {
    setAccountPanel(panel);
    setProfileOpen(false);
    setNotificationsOpen(false);
    apiGet<{ account: AccountInfo }>(`/answerer/account-security?userId=${encodeURIComponent(userName)}`)
      .then(res => setAccountInfo(res.account)).catch(() => setAccountInfo({ userId: userName, name: userName }));
  };
  const savePreferences = () => {
    localStorage.setItem("shine_default_view", gridView ? "grid" : "list");
    localStorage.setItem("shine_email_notifications", emailNotifications ? "on" : "off");
    setPasswordMessage("Preferences saved successfully.");
  };
  const changePassword = async () => {
    if (!currentPassword || newPassword.length < 4) { setPasswordMessage("Enter your current password and a new password of at least 4 characters."); return; }
    setSavingPassword(true); setPasswordMessage("");
    try {
      await apiPost("/auth/change-password", { userId: userName, oldPassword: currentPassword, newPassword, role: "answerer" });
      setCurrentPassword(""); setNewPassword(""); setPasswordMessage("Password changed successfully.");
    } catch (error: any) { setPasswordMessage(error?.message || "Password could not be changed."); }
    finally { setSavingPassword(false); }
  };

  const startExam = async (examId: string) => {
    setStartingId(examId);
    try {
      const res = await apiGet<{ test: ExamForTaking }>(`/answerer/tests/${examId}?userId=${encodeURIComponent(userName)}`);
      if (!res.test.questions || res.test.questions.length === 0) {
        alert("This test has no questions configured yet. Please contact your administrator.");
        return;
      }
      setActiveExam(res.test);
      navigate("/dashboard/tests");
    } catch (error) {
      console.error(error);
      alert("The test could not be started. Please check that it is active and try again.");
    } finally { setStartingId(""); }
  };

  const exitExam = () => {
    setActiveExam(null);
    navigate("/dashboard");
    loadPortal();
  };

  const categoryFor = (test: AssignedTest): TestTab => {
    if (test.attempted || test.status === "completed") return "completed";
    if (test.status === "expired") return "missed";
    if (test.status === "upcoming" || test.status === "draft") return "upcoming";
    return "active";
  };

  const visibleTests = useMemo(() => tests.filter(test => {
    const category = categoryFor(test);
    const matchesTab = category === testTab;
    const testName = test.name.toLowerCase();
    const matchesExam = !selectedExamPage || (test.categoryId===selectedExamPage.categoryId && test.subcategoryId===selectedExamPage.subcategoryId && test.stage===selectedExamPage.stage);
    return matchesTab && matchesExam && testName.includes(search.toLowerCase());
  }), [tests, testTab, search, selectedExamPage]);

  const scopedTests = (tab: TestTab) => tests.filter(test => {
    const matchesExam = !selectedExamPage || (test.categoryId===selectedExamPage.categoryId && test.subcategoryId===selectedExamPage.subcategoryId && test.stage===selectedExamPage.stage);
    return matchesExam && categoryFor(test) === tab;
  });
  const filteredHistory = history.filter(item => normalizeSearchText(`${item.testName} ${item.submittedAt ? new Date(item.submittedAt).toLocaleDateString("en-IN") : ""} ${item.percentage}% ${item.passed ? "passed" : "needs improvement"}`).includes(normalizeSearchText(reportSearch)));

  const formatDate = (value: string) => new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const validityDate = (value?: string) => value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "No expiry";
  const formatTime = (seconds: number) => `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

  const [studentSidebarCollapsed, setStudentSidebarCollapsed] = useState(() => localStorage.getItem("shine_student_sidebar_collapsed") === "true");
  const [studentMobileOpen, setStudentMobileOpen] = useState(false);
  const closeStudentMobile = () => setStudentMobileOpen(false);

  const toggleStudentSidebar = () => {
    setStudentSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("shine_student_sidebar_collapsed", String(next));
      return next;
    });
  };

  const handleGoTo = (next: PortalView) => {
    goTo(next);
    closeStudentMobile();
  };

  const handleOpenExamPage = (page: ExamPage) => {
    openExamPage(page);
    closeStudentMobile();
  };

  const isDesktopCollapsed = studentSidebarCollapsed && !studentMobileOpen;

  if (activeExam) return (
    <TestInterface
      userId={userName}
      examId={activeExam.id}
      testName={activeExam.testName}
      duration={activeExam.duration}
      passingPercentage={activeExam.passingPercentage}
      timerMode={activeExam.timerMode}
      sectionConfig={activeExam.sectionConfig}
      questions={activeExam.questions}
      negativeMarkingScheme={activeExam.negativeMarkingScheme}
      negativeMarks={activeExam.negativeMarks}
      isSeries={activeExam.isSeries}
      seriesId={activeExam.seriesId}
      paperId={activeExam.paperId}
      customInstructions={activeExam.customInstructions}
      onExit={exitExam}
    />
  );

  const currentReport = selectedReport || history[0];
  const score = currentReport?.scoredMarks || 0;
  const total = currentReport?.totalMarks || 0;
  const pct = currentReport?.percentage || 0;
  const review = reportDetail?.questionReview || [];
  const answered = (value: string | string[]) => Array.isArray(value) ? value.length > 0 : Boolean(value);
  const questionCount = review.length || total;
  const correct = review.length ? review.filter(item => item.isCorrect).length : (total ? Math.max(0, Math.round(score)) : 0);
  const incorrect = review.length ? review.filter(item => !item.isCorrect && answered(item.userAnswer)).length : (total ? Math.max(0, Math.min(total - correct, Math.round(total * .35))) : 0);
  const unanswered = Math.max(0, questionCount - correct - incorrect);

  return (
    <div className="candidate-shell">
      {/* Mobile Drawer Overlay Backdrop */}
      <div
        className={`candidate-mobile-overlay ${studentMobileOpen ? "visible" : ""}`}
        onClick={closeStudentMobile}
      />

      <aside className={`candidate-sidebar ${isDesktopCollapsed ? "collapsed" : ""} ${studentMobileOpen ? "sidebar-open" : ""}`}>
        <div className="candidate-logo">
          <button 
            type="button" 
            className="sidebar-hamburger-btn" 
            onClick={toggleStudentSidebar} 
            title={isDesktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <ShineLogo compact={isDesktopCollapsed} />
          <button
            type="button"
            className="student-drawer-close-btn"
            onClick={closeStudentMobile}
            aria-label="Close navigation"
          >
            ✕
          </button>
        </div>

        <nav className="candidate-nav">
          <div className={`my-tests-nav ${view === "tests" ? "active" : ""}`} title={isDesktopCollapsed ? "My Tests" : ""}>
            <button className="my-tests-main" onClick={() => handleGoTo("tests")}>
              <span className="candidate-nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="m9 15 2 2 4-4" />
                </svg>
              </span>
              {!isDesktopCollapsed && <span>My Tests</span>}
            </button>
            {!isDesktopCollapsed && <button className={`my-tests-collapse ${testsTreeOpen ? "open" : ""}`} onClick={() => setTestsTreeOpen(open => !open)} aria-label={testsTreeOpen ? "Collapse My Tests" : "Expand My Tests"}>⌄</button>}
          </div>
          {testsTreeOpen && !isDesktopCollapsed && <div className="exam-categories under-tests">
            {examGroups.map(group => <div className="exam-group" key={group.slug}>
              <button className={`exam-group-button ${expandedGroups.includes(group.slug) ? "open" : ""}`} onClick={() => toggleExpanded(group.slug, expandedGroups, setExpandedGroups)}>
                <span className="dot-icon" />
                <span className="group-label">{group.label}</span>
                <svg className={`group-chevron ${expandedGroups.includes(group.slug) ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {expandedGroups.includes(group.slug) && <div className="exam-group-children">{group.exams.map(exam => <div className="exam-subgroup" key={exam.slug}>
                <button className={`exam-name-button ${expandedExams.includes(exam.slug) ? "open" : ""} ${selectedExamPage?.examSlug === exam.slug ? "selected" : ""}`} onClick={() => toggleExpanded(exam.slug, expandedExams, setExpandedExams)}>
                  <svg className={`subgroup-chevron ${expandedExams.includes(exam.slug) ? "open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  <b className="exam-label">{exam.label}</b>
                </button>
                {expandedExams.includes(exam.slug) && <div className="exam-stage-links">{examPages.filter(page => page.subcategoryId === exam.id).map(page => <button key={page.stageSlug} className={selectedExamPage?.subcategoryId === page.subcategoryId && selectedExamPage.stageSlug === page.stageSlug ? "active" : ""} onClick={() => handleOpenExamPage(page)}><i className="stage-dot" />{page.stageLabel}</button>)}</div>}
              </div>)}</div>}
            </div>)}
          </div>}
          {navItems.filter(item => item.view !== "tests").map(item => <button key={item.view} className={`candidate-nav-item ${view === item.view ? "active" : ""}`} onClick={() => handleGoTo(item.view)} title={isDesktopCollapsed ? item.label : ""}><span className="candidate-nav-icon">{item.icon}</span>{!isDesktopCollapsed && <span>{item.label}</span>}</button>)}
        </nav>

        {!isDesktopCollapsed && (
          <div className="candidate-sidebar-footer">
            <div className="support-card"><span>?</span><div><strong>Need help?</strong><small>Visit our support center</small></div></div>
            <small>VICTORY STUDY CIRCLE • v2.0</small>
          </div>
        )}
      </aside>

      <main className={`candidate-main ${isDesktopCollapsed ? "collapsed" : ""}`}>
        <header className="candidate-topbar">
          <div className="topbar-left-brand">
            <button
              type="button"
              className={`candidate-hamburger-btn ${studentMobileOpen ? "open" : ""}`}
              onClick={() => setStudentMobileOpen(o => !o)}
              aria-label="Toggle student navigation menu"
            >
              <span /><span /><span />
            </button>
            <div className="topbar-title-wrap">
              <span className="mobile-brand">{tenant.brandTitle || tenant.name || 'Exam Portal'}</span>
              <h1>{view === "tests" ? (selectedExamPage ? `${selectedExamPage.examLabel} ${selectedExamPage.stageLabel}` : "My Tests") : view === "report" ? "Performance Report" : navItems.find(n => n.view === view)?.label}</h1>
            </div>
          </div>
          <div className="candidate-top-actions">
            {view === "tests" && <div className="portal-value-help"><ValueHelpField label="Search Tests" placeholder="Search assigned tests" value={search} options={tests.map(test=>({value:test.name,label:test.name,keywords:[`${test.duration} minutes`,`${test.questions} questions`]}))} onChange={setSearch} allowFreeText compact/></div>}
            <div className="notification-wrap" ref={(el) => {
              if (!el) return;
              const handler = (e: MouseEvent) => {
                if (!el.contains(e.target as Node)) setNotificationsOpen(false);
              };
              document.addEventListener('mousedown', handler);
              return () => document.removeEventListener('mousedown', handler);
            }}>
              <button
                className={`icon-button notif-bell-btn ${notificationsOpen ? 'active' : ''}`}
                aria-label={`Notifications${unreadNotifications > 0 ? ` (${unreadNotifications} unread)` : ''}`}
                onClick={() => { setNotificationsOpen(v => !v); setProfileOpen(false); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                {unreadNotifications > 0 && <span className="notif-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>}
              </button>
              {notificationsOpen && (
                <div className="notification-panel" role="dialog" aria-label="Notifications">
                  {/* Panel Header */}
                  <div className="notif-panel-header">
                    <div className="notif-panel-header-top">
                      <div className="notif-panel-title-row">
                        <div className="notif-panel-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                        </div>
                        <h4 className="notif-panel-title">Notifications</h4>
                        {unreadNotifications > 0 && <span className="notif-panel-count-badge">{unreadNotifications} new</span>}
                      </div>
                      <button
                        className="notif-panel-close-btn"
                        onClick={() => setNotificationsOpen(false)}
                        aria-label="Close notifications"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                    {notifications.length > 0 && (
                      <div className="notif-panel-actions">
                        <button className="notif-action-btn" onClick={() => markNotificationRead('all')}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Mark all read
                        </button>
                        <button className="notif-action-btn notif-action-danger" onClick={() => void clearNotifications('all')}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                          Clear all
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Notification List */}
                  <div className="notif-panel-list">
                    {notifications.length === 0 ? (
                      <div className="notif-empty-state">
                        <div className="notif-empty-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                        </div>
                        <p className="notif-empty-title">All caught up!</p>
                        <p className="notif-empty-sub">No new notifications at the moment.</p>
                      </div>
                    ) : notifications.map((item) => {
                      const typeConfig = {
                        test: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'New Test',
                          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg> },
                        result: { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', label: 'Result',
                          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
                        document: { color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', label: 'Document',
                          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg> },
                        announcement: { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Notice',
                          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
                      }[item.type] ?? { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', label: 'Info', icon: null };

                      const relativeTime = (() => {
                        if (!item.createdAt) return '';
                        const diff = Date.now() - new Date(item.createdAt).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'Just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })();

                      return (
                        <div
                          key={item.id}
                          className={`notif-item ${item.read ? 'notif-read' : 'notif-unread'}`}
                        >
                          {!item.read && <span className="notif-unread-dot" aria-hidden="true" />}
                          <button
                            className="notif-item-btn"
                            onClick={() => { void markNotificationRead(item.id); setNotificationsOpen(false); goTo(item.target); }}
                          >
                            <span
                              className="notif-item-icon"
                              style={{ color: typeConfig.color, background: typeConfig.bg, borderColor: typeConfig.border }}
                            >
                              {typeConfig.icon}
                            </span>
                            <div className="notif-item-body">
                              <div className="notif-item-meta">
                                <span className="notif-type-label" style={{ color: typeConfig.color }}>{typeConfig.label}</span>
                                <span className="notif-timestamp">{relativeTime}</span>
                              </div>
                              <strong className="notif-item-title">{item.title}</strong>
                              <p className="notif-item-msg">{item.message}</p>
                            </div>
                          </button>
                          <button
                            className="notif-dismiss-btn"
                            onClick={(e) => { e.stopPropagation(); void clearNotifications(item.id); }}
                            title="Dismiss"
                            aria-label="Dismiss notification"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="profile-wrap">
              <button className="profile-button" onClick={() => { setProfileOpen(v => !v); setNotificationsOpen(false); }}>
                <span>{userName.charAt(0).toUpperCase()}</span>
                <div>
                  <strong>{userName}</strong>
                  <small>Candidate</small>
                </div>
                <svg className={`profile-chevron ${profileOpen ? "open" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {profileOpen && (
                <div className="profile-menu" role="menu" aria-label="Candidate account menu">
                  <div className="profile-menu-user">
                    <span className="profile-menu-avatar">{userName.charAt(0).toUpperCase()}</span>
                    <div className="profile-menu-info">
                      <strong className="profile-menu-name">{userName}</strong>
                      <span className="profile-menu-role">Candidate account</span>
                    </div>
                  </div>
                  <div className="profile-menu-divider" />
                  <div className="profile-menu-links">
                    <button className="profile-menu-item" onClick={() => { setProfileOpen(false); openAccountPanel("profile"); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <span>My profile</span>
                    </button>
                    <button className="profile-menu-item" onClick={() => { setProfileOpen(false); openAccountPanel("settings"); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      <span>Settings & password</span>
                    </button>
                  </div>
                  <div className="profile-menu-divider" />
                  <button className="profile-menu-item signout" onClick={() => { setProfileOpen(false); onLogout(); }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {view === "tests" && <section className="portal-page tests-page">
          <div className="test-tabs" role="tablist" aria-label="Test status filters">
            {(["active", "upcoming", "missed", "completed"] as TestTab[]).map(tab => (
              <button
                key={tab}
                role="tab"
                aria-selected={testTab === tab}
                className={`test-tab-btn ${testTab === tab ? "active" : ""}`}
                onClick={() => setTestTab(tab)}
              >
                <span className="test-tab-icon">{tab === "active" ? "◉" : tab === "upcoming" ? "◷" : tab === "missed" ? "⊘" : "✓"}</span>
                <span className="test-tab-label">{tab[0].toUpperCase()+tab.slice(1)}</span>
                <i className="test-tab-badge">{selectedExamPage ? scopedTests(tab).length : tests.filter(t => categoryFor(t) === tab).length}</i>
              </button>
            ))}
          </div>
          <div className="page-toolbar">
            <div>
              <div className="exam-breadcrumb">
                <button onClick={() => goTo("tests")}>My Tests</button>
                {selectedExamPage && <><span>›</span><span>{selectedExamPage.groupLabel}</span><span>›</span><span>{selectedExamPage.examLabel}</span><span>›</span><b>{selectedExamPage.stageLabel}</b></>}
              </div>
              <h2>{selectedExamPage ? `${selectedExamPage.examLabel} ${selectedExamPage.stageLabel}` : `${testTab[0].toUpperCase()+testTab.slice(1)} Tests`}</h2>
              <p>{testTab === "completed" ? "Review your completed assessments and reports" : "Assessments available for your account"}</p>
            </div>
            <div className="view-toggle">
              <button className={gridView ? "active" : ""} onClick={() => setGridView(true)} title="Grid View">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </button>
              <button className={!gridView ? "active" : ""} onClick={() => setGridView(false)} title="List View">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
            </div>
          </div>
          {loading ? <div className="portal-empty"><span className="loader" />Loading your assessments…</div> : visibleTests.length === 0 ? <div className="portal-empty"><span>◇</span><h3>No {testTab} tests</h3><p>There are no assessments in this section right now.</p></div> :
          <>
            {tests.some(t => t.attemptStatus === "in_progress") && testTab === "active" && (
              <div className="active-resume-hero-banner">
                <div className="resume-hero-left">
                  <span className="resume-pulse-dot" />
                  <div>
                    <span className="resume-hero-badge">ACTION REQUIRED • IN-PROGRESS EXAM</span>
                    <h4>
                      {tests.find(t => t.attemptStatus === "in_progress")?.name}
                    </h4>
                    <p>
                      You paused this test at <strong>Question #{(tests.find(t => t.attemptStatus === "in_progress")?.currentQuestionIndex || 0) + 1}</strong>.
                      All your answers and countdown are safely preserved.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-resume-hero-action"
                  disabled={!!startingId}
                  onClick={() => {
                    const ipTest = tests.find(t => t.attemptStatus === "in_progress");
                    if (ipTest) startExam(ipTest.id);
                  }}
                >
                  {startingId ? "Restoring Test…" : "▶ Resume Exam Now"}
                </button>
              </div>
            )}
            <div className={`portal-test-list ${gridView ? "grid" : "list"}`}>{visibleTests.map((test, index) => (
              <article className={`portal-test-card ${test.attemptStatus === "in_progress" ? "is-in-progress" : ""}`} key={test.id}>
              <div className={`test-accent accent-${index % 4}`} />
              <button className={`test-bookmark-button ${bookmarks.some(item=>item.type==="test"&&item.testId===test.id)?"saved":""}`} onClick={()=>void toggleTestBookmark(test)} aria-label="Bookmark test" title="Bookmark test">{bookmarks.some(item=>item.type==="test"&&item.testId===test.id)?"★":"☆"}</button>
              <div className="test-card-heading">
                <div className="test-heading-top">
                  <span>{index % 2 ? "QUANTITATIVE" : "GENERAL"}</span>
                  {test.attemptStatus === "in_progress" && (
                    <span className="in-progress-badge">⚡ IN PROGRESS</span>
                  )}
                </div>
                <h3>{test.name}</h3>
              </div>
              <div className="test-validity-box"><div><small>Start</small><strong>{validityDate(test.availableFrom)}</strong></div><div><small>End</small><strong>{validityDate(test.validUntil)}</strong></div><div><small>Questions</small><strong>{test.questions}</strong></div><div><small>Duration</small><strong>{test.duration} minutes</strong></div></div>
              {test.attemptStatus === "in_progress" && (
                <div className="test-resume-progress">
                  <div className="test-resume-info">
                    <span>Stopped at Q{(test.currentQuestionIndex || 0) + 1}</span>
                    <strong>{test.answeredCount || 0} / {test.totalQuestions || test.questions} Answered</strong>
                  </div>
                  <div className="test-resume-bar">
                    <div className="test-resume-fill" style={{ width: `${Math.min(100, Math.round(((test.answeredCount || 0) / (test.totalQuestions || test.questions || 1)) * 100))}%` }} />
                  </div>
                </div>
              )}
              {testTab === "completed" ? (
                <button className="shine-primary" onClick={() => { const r=history.find(h => h.examId === test.id) || history[0]; setSelectedReport(r || null); setReportTab("score"); goTo("report"); }}>View report <span>→</span></button>
              ) : testTab === "upcoming" ? (
                <button className="shine-primary" disabled>Opens {validityDate(test.availableFrom)}</button>
              ) : testTab === "missed" ? (
                <button className="shine-primary" disabled>Expired</button>
              ) : test.attemptStatus === "in_progress" ? (
                <button className="shine-primary resume-btn" disabled={!!startingId} onClick={() => startExam(test.id)}>
                  {startingId === test.id ? "Resuming…" : `Resume test (Q${(test.currentQuestionIndex || 0) + 1})`} <span>→</span>
                </button>
              ) : (
                <button className="shine-primary" disabled={!!startingId} onClick={() => startExam(test.id)}>
                  {startingId === test.id ? "Starting…" : "Start test"} <span>→</span>
                </button>
              )}
            </article>
          ))}</div></>}

        </section>}

        {view === "report" && <section className="portal-page report-page">
          <div className="report-selector-card">
            <div className="report-selector-left">
              <span className="report-badge">REPORT FOR</span>
              <div className="report-search-wrap">
                <ValueHelpField
                  label="Select Assessment Report"
                  placeholder="Search assessment, date or score..."
                  value={currentReport?.attemptId || ""}
                  options={history.map((h) => ({
                    value: h.attemptId,
                    label: `${h.testName} (${formatDate(h.submittedAt)})`,
                    keywords: [
                      `${h.percentage}% Score`,
                      h.passed ? "Passed" : "Needs improvement",
                      formatDate(h.submittedAt),
                    ],
                  }))}
                  onChange={(attemptId) => {
                    const found = history.find((h) => h.attemptId === attemptId);
                    if (found) {
                      setSelectedReport(found);
                      setReportSearch(found.testName);
                    }
                  }}
                  compact
                />
              </div>
            </div>
            {currentReport && (
              <div className="report-selector-right">
                <span className={`result-pill ${currentReport.passed ? "pass" : "fail"}`}>
                  {currentReport.passed ? "✓ Passed" : "Needs Improvement"} ({currentReport.percentage}%)
                </span>
              </div>
            )}
          </div>
          <div className="report-tabs">{([['score','Score Card'],['subject','Subject Report'],['solution','Solution Report'],['questions','Question Report'],['compare','Compare Yourself']] as [ReportTab,string][]).map(([id,label]) => <button className={reportTab === id ? "active" : ""} onClick={() => setReportTab(id)} key={id}>{label}</button>)}</div>
          {!currentReport ? <div className="portal-empty"><span>▤</span><h3>No report available</h3><p>Complete a test and your detailed analytics will appear here.</p></div> : <>
            <div className="report-kpis"><div><span>Total Questions</span><strong>{questionCount}</strong></div><div><span>Total Marks</span><strong>{fmt(total)}</strong></div><div><span>Time Spent</span><strong>{formatTime(currentReport.timeSpentSec)}</strong></div><div><span>Percentile</span><strong>{Math.min(99, Math.round(60 + pct * .35))}<small>th</small></strong></div></div>
            {reportTab === "score" && <ScoreReport score={score} total={total} pct={pct} correct={correct} incorrect={incorrect} unanswered={unanswered} />}
            {reportTab === "subject" && <SubjectReport score={score} total={total} pct={pct} questionCount={questionCount} time={formatTime(currentReport.timeSpentSec)} review={review} />}
            {reportTab === "solution" && <SolutionReport testName={currentReport.testName} userName={userName} review={review} onClose={() => setReportTab("score")} />}
            {reportTab === "questions" && <QuestionReport total={questionCount} correct={correct} review={review} />}
            {reportTab === "compare" && <CompareReport score={score} total={total} pct={pct} />}
          </>}
        </section>}

        {view === "classes" && <StudentClasses userId={userName} />}
        {view === "bookmarks" && <CandidateBookmarks userId={userName} bookmarks={bookmarks} onChanged={loadBookmarks} onOpenTest={openBookmarkedTest} />}
        {view === "documents" && <CandidateDocuments userId={userName} />}
        {view === "announcements" && <CandidateAnnouncements userId={userName} />}
        {view === "current-affairs" && <CurrentAffairsHub isAdmin={false} userName={userName} />}
        {view === "performance" && <PerformanceHeatmap userName={userName} />}
        {view === "test-series" && (
          <StudentTestSeriesView
            userName={userName}
            onStartExam={(examData: any) => {
              setActiveExam({
                id: examData.paperId || examData.id,
                testName: examData.testName,
                duration: examData.duration,
                passingPercentage: examData.passingPercentage,
                questions: examData.questions || [],
                timerMode: examData.timerMode || "overall",
                sectionConfig: examData.sectionConfig || [],
                negativeMarkingScheme: examData.negativeMarkingScheme || "quarter",
                negativeMarks: examData.negativeMarks,
                isSeries: true,
                seriesId: examData.seriesId,
                paperId: examData.paperId || examData.id,
                customInstructions: examData.instructions || examData.customInstructions,
              });
              navigate("/dashboard/tests");
            }}
          />
        )}
      </main>

      {/* Native Play Store Mobile Bottom Navigation Bar */}
      <nav className="mobile-app-bottom-nav" aria-label="Mobile Navigation">
        {navItems.map((item) => (
          <button
            key={item.view}
            type="button"
            className={`mobile-bottom-nav-item ${view === item.view ? "active" : ""}`}
            onClick={() => handleGoTo(item.view)}
          >
            <div className="mobile-bottom-nav-icon-wrap">
              {item.icon}
              {item.view === "announcements" && unreadNotifications > 0 && (
                <span className="mobile-bottom-nav-badge">{unreadNotifications}</span>
              )}
            </div>
            <span className="mobile-bottom-nav-label">{item.label}</span>
          </button>
        ))}
      </nav>

      {accountPanel && <div className="account-modal-backdrop" onMouseDown={() => setAccountPanel(null)}><section className="account-modal" onMouseDown={event => event.stopPropagation()}>
        <header><div><span>{accountPanel === "profile" ? "CANDIDATE ACCOUNT" : "ACCOUNT SETTINGS"}</span><h2>{accountPanel === "profile" ? "My Profile" : "Settings & Password"}</h2></div><button onClick={() => setAccountPanel(null)}>×</button></header>
        {accountPanel === "profile" ? <div className="profile-details"><div className="large-avatar">{userName.charAt(0).toUpperCase()}</div><h3>{accountInfo?.name || userName}</h3><p>Shine Candidate</p><dl><div><dt>User ID</dt><dd>{accountInfo?.userId || userName}</dd></div><div><dt>Email</dt><dd>{accountInfo?.email || "Not provided"}</dd></div><div><dt>College email</dt><dd>{accountInfo?.collegeEmail || "Not provided"}</dd></div><div><dt>Account status</dt><dd className="active-account">● {accountInfo?.isActive === false ? "Inactive" : "Active"}</dd></div></dl></div> : <div className="settings-content">
          <div className="settings-section"><h3>Preferences</h3><label className="setting-row"><div><strong>Email notifications</strong><small>Receive assessment and report updates.</small></div><input type="checkbox" checked={emailNotifications} onChange={e => setEmailNotifications(e.target.checked)} /></label><label className="setting-row"><div><strong>Default grid view</strong><small>Show tests as cards instead of a list.</small></div><input type="checkbox" checked={gridView} onChange={e => setGridView(e.target.checked)} /></label><button className="settings-save" onClick={savePreferences}>Save preferences</button></div>
          <div className="settings-section"><h3>Change password</h3><label>Current password<input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} /></label><label>New password<input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} /></label><button className="settings-save" disabled={savingPassword} onClick={changePassword}>{savingPassword ? "Updating…" : "Update password"}</button>{passwordMessage && <p className="settings-message">{passwordMessage}</p>}</div>
        </div>}
      </section></div>}
    </div>
  );
};

const fmt = (val: any) => {
  const n = typeof val === 'number' ? val : parseFloat(String(val ?? 0));
  if (isNaN(n)) return '0';
  return Number.isInteger(n) ? n.toString() : Number(n.toFixed(1)).toString();
};

const Metric=({label,value,max,color}:any)=><div className="metric-row"><div><span>{label}</span><b>{fmt(value)}/{fmt(max)}</b></div><div className="metric-track"><i style={{width:`${max ? (parseFloat(value)/parseFloat(max))*100 : 0}%`,background:color}} /></div></div>;
const ScoreReport = ({score,total,pct,correct,incorrect,unanswered}:any) => <div className="report-grid"><article className="report-card score-summary"><h3>Performance Summary</h3><div className="score-ring" style={{'--score': `${pct * 3.6}deg`} as React.CSSProperties}><div><strong>{fmt(score)}</strong><span>out of {fmt(total)}</span></div></div><h2>{pct >= 70 ? "Excellent work!" : pct >= 40 ? "Good progress" : "Keep practicing"}</h2><p>You scored <b>{pct.toFixed(1)}%</b> in this assessment.</p><div className="score-legend"><span className="correct"><i />Correct <b>{correct}</b></span><span className="wrong"><i />Incorrect <b>{incorrect}</b></span><span><i />Unattempted <b>{unanswered}</b></span></div></article><article className="report-card"><h3>Score Breakdown</h3><div className="horizontal-metrics"><Metric label="Correct answers" value={correct} max={fmt(total)} color="#27b487"/><Metric label="Incorrect answers" value={incorrect} max={fmt(total)} color="#ef6a78"/><Metric label="Unattempted" value={unanswered} max={fmt(total)} color="#a7b0c0"/></div><div className="rank-strip"><div><small>Your rank</small><strong>#{Math.max(1,Math.round(400-pct*3))}</strong></div><div><small>Accuracy</small><strong>{pct.toFixed(1)}%</strong></div><div><small>Average score</small><strong>{fmt(total*.52)}</strong></div></div></article></div>;
const SubjectReport = ({ score, total, pct, time, questionCount, review }: any) => {
  const sectionStats = useMemo(() => {
    if (!review || !Array.isArray(review) || review.length === 0) {
      return [{
        name: 'General Aptitude',
        totalQuestions: questionCount || 0,
        scoredMarks: score || 0,
        maxMarks: total || 0,
        accuracy: pct || 0,
        timeSpent: time || '—'
      }];
    }

    const groups: Record<string, { totalQuestions: number; correct: number; attempted: number; scoredMarks: number; maxMarks: number }> = {};

    review.forEach((item: any) => {
      const secName = item.section || 'General';
      if (!groups[secName]) {
        groups[secName] = { totalQuestions: 0, correct: 0, attempted: 0, scoredMarks: 0, maxMarks: 0 };
      }
      groups[secName].totalQuestions += 1;
      const qMarks = Number(item.marks || 1);
      groups[secName].maxMarks += qMarks;

      const isAns = Array.isArray(item.userAnswer) ? item.userAnswer.length > 0 : (item.userAnswer !== undefined && item.userAnswer !== null && item.userAnswer !== '');
      if (isAns) {
        groups[secName].attempted += 1;
      }
      if (item.isCorrect) {
        groups[secName].correct += 1;
        groups[secName].scoredMarks += qMarks;
      }
    });

    return Object.entries(groups).map(([secName, data]) => {
      const acc = data.attempted > 0 ? (data.correct / data.attempted) * 100 : 0;
      return {
        name: secName,
        totalQuestions: data.totalQuestions,
        scoredMarks: data.scoredMarks,
        maxMarks: data.maxMarks,
        accuracy: acc,
        timeSpent: time || '—'
      };
    });
  }, [review, questionCount, score, total, pct, time]);

  return (
    <div className="report-card table-card">
      <h3>Subject Performance & Section Breakdown</h3>
      <div className="report-table subject-table">
        <div className="table-head">
          <span>Subject / Section Name</span>
          <span>Questions</span>
          <span>Score</span>
          <span>Accuracy</span>
          <span>Time spent</span>
        </div>
        {sectionStats.map((sec, idx) => (
          <div className="table-row" key={sec.name || idx}>
            <strong>{sec.name}</strong>
            <span>{sec.totalQuestions}</span>
            <span>{fmt(sec.scoredMarks)} / {fmt(sec.maxMarks)}</span>
            <span>{sec.accuracy.toFixed(1)}%</span>
            <span>{sec.timeSpent}</span>
          </div>
        ))}
      </div>
      <div className="subject-analysis">
        <div>
          <span>Overall Test score</span>
          <strong>{fmt(score)}<small>/{fmt(total)}</small></strong>
          <Metric label="Mastery" value={pct} max={100} color="#2f6fed" />
        </div>
        <div className="mini-pie" style={{ '--score': `${pct * 3.6}deg` } as React.CSSProperties} />
      </div>
    </div>
  );
};

const reviewStatus=(item:any,index:number,correct:number,incorrect:number)=>item ? (item.isCorrect?'correct':(Array.isArray(item.userAnswer)?item.userAnswer.length>0:Boolean(item.userAnswer))?'wrong':'empty') : index<correct?'correct':index<correct+incorrect?'wrong':'empty';
const SolutionReport = ({ testName, userName, review, onClose }: any) => {
  const sections = Array.from(new Set((review || []).map((item: any) => item.section).filter(Boolean))) as string[];
  const [activeSection, setActiveSection] = useState<string>(sections[0] || (review[0]?.section ?? ''));
  const [sectionIndex, setSectionIndex] = useState<number>(0);

  const tenantOrg = (() => {
    try {
      const ti = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("tenant_info") : null;
      if (ti) {
        const p = JSON.parse(ti);
        return p.brandTitle || p.name || "";
      }
    } catch {}
    return (
      (typeof sessionStorage !== "undefined" &&
        (sessionStorage.getItem("tenantBrandTitle") ||
          sessionStorage.getItem("tenantName") ||
          sessionStorage.getItem("orgName"))) ||
      ""
    );
  })();

  const [watermarkConfig, setWatermarkConfig] = useState<{
    enabled: boolean;
    text: string;
    color: string;
    opacity: number;
    includeCandidate: boolean;
    includeTimestamp: boolean;
  }>({
    enabled: true,
    text: tenantOrg ? `${tenantOrg} • CONFIDENTIAL SOLUTION REPORT` : "CONFIDENTIAL SOLUTION REPORT",
    color: "#dc2626",
    opacity: 0.25,
    includeCandidate: true,
    includeTimestamp: true,
  });

  useEffect(() => {
    apiGet<any>("/public/security/config")
      .then((cfg) => {
        if (cfg) {
          const isMaster = cfg.watermarkEnabled !== false;
          const isReport = cfg.solutionReportWatermarkEnabled !== false;
          const isModule = !Array.isArray(cfg.watermarkModules) || cfg.watermarkModules.includes("results");
          const enabled = isMaster && isReport && isModule;

          let text = (cfg.solutionReportWatermarkText || "").trim();
          if (!text) {
            text = tenantOrg ? `${tenantOrg} • CONFIDENTIAL SOLUTION REPORT` : "CONFIDENTIAL SOLUTION REPORT";
          } else if (tenantOrg) {
            text = text.replace(/SHINE\s+EXAM/gi, tenantOrg).replace(/SHINE/gi, tenantOrg);
          }

          setWatermarkConfig({
            enabled,
            text,
            color: cfg.solutionReportWatermarkColor || "#dc2626",
            opacity: typeof cfg.solutionReportWatermarkOpacity === "number" ? cfg.solutionReportWatermarkOpacity : 0.25,
            includeCandidate: cfg.solutionReportWatermarkIncludeCandidate !== false,
            includeTimestamp: cfg.solutionReportWatermarkIncludeTimestamp !== false,
          });
        }
      })
      .catch(() => {});
  }, [tenantOrg]);

  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = origOverflow;
    };
  }, []);

  const sectionQuestions = (review || []).filter((item: any) => item.section === activeSection);
  const activeQuestions = sectionQuestions.length > 0 ? sectionQuestions : review;
  const safeIndex = Math.min(sectionIndex, Math.max(0, activeQuestions.length - 1));
  const current = activeQuestions[safeIndex] || review[0];

  const answered = (value: any) => Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== "";
  const status = (item: any) => !answered(item?.userAnswer) ? "empty" : item?.isCorrect ? "correct" : "wrong";

  const matches = (value: any, option: string, index: number, optionsList?: string[]) => {
    const rawValues = (Array.isArray(value) ? value : [value]).map(v => String(v ?? "").trim().toLowerCase()).filter(Boolean);
    if (rawValues.length === 0) return false;
    const targetOpt = option.trim().toLowerCase();

    // 1. If values match an exact option string in the options list, match by exact option string ONLY
    if (optionsList && optionsList.some(opt => rawValues.includes(opt.trim().toLowerCase()))) {
      return rawValues.includes(targetOpt);
    }

    // 2. Direct string match
    if (rawValues.includes(targetOpt)) {
      return true;
    }

    // 3. Fallback to index letter (A=0, B=1, C=2...) if no option text matched
    const letterIndex = String.fromCharCode(97 + index);
    return rawValues.includes(letterIndex);
  };

  const handleSectionClick = (sec: string) => {
    setActiveSection(sec);
    setSectionIndex(0);
  };

  if (!current) return <div className="solution-paper-empty"><button onClick={onClose}>← Back to score card</button><h3>Solution paper is not available for this attempt.</h3></div>;

  return (
    <div className="solution-paper-overlay">
      <div className="solution-paper">
        {/* ── Dynamic Bold Colored Solution Watermark ── */}
        {watermarkConfig.enabled && (
          <DynamicWatermark
            userId={userName}
            customText={watermarkConfig.text}
            color={watermarkConfig.color}
            opacity={watermarkConfig.opacity}
            isBold={true}
            includeCandidate={watermarkConfig.includeCandidate}
            includeTimestamp={watermarkConfig.includeTimestamp}
          />
        )}

        <header className="solution-paper-header">
          <div><ShineLogo compact /><h2>{testName}</h2></div>
          <div><span className="solution-avatar">{String(userName).charAt(0).toUpperCase()}</span><strong>{userName}</strong><button onClick={onClose}>Exit solution</button></div>
        </header>

        <div className="solution-section-bar">
          {sections.map((section) => (
            <button key={section} className={activeSection === section ? "active" : ""} onClick={() => handleSectionClick(section)}>
              {section}
            </button>
          ))}
        </div>

        <div className="solution-question-meta">
          <strong>Question No. {safeIndex + 1}</strong>
          <div>
            <span className="difficulty-pill">{activeSection || "General"}</span>
            <span className={status(current)}>
              {status(current) === "correct" ? "Correct" : status(current) === "wrong" ? "Incorrect" : "Unattempted"}
            </span>
          </div>
        </div>

        <main className="solution-paper-body">
          <section className="solution-question-pane">
            <ParsedQuestionPreview
              question={current.question || `Question ${safeIndex + 1}`}
              context={current.context}
              contextType={current.contextType}
              chartData={current.chartData}
              tableData={current.tableData}
              imageReference={current.imageReference}
              visualReferences={current.visualReferences}
              mappingStatus={current.mappingStatus}
              mappingConfidence={current.mappingConfidence}
            />
            <div className="solution-options">
              {(current.options || []).map((option: string, index: number) => {
                const correct = matches(current.correctAnswer, option, index, current.options);
                const chosen = matches(current.userAnswer, option, index, current.options);
                return (
                  <div key={`${option}-${index}`} className={`solution-option ${correct ? "correct-option" : ""} ${chosen && !correct ? "wrong-option" : ""}`}>
                    <span>{String.fromCharCode(65 + index)}</span>
                    <p>{option}</p>
                    {correct && <b>✓ Correct answer</b>}
                    {chosen && correct && <em>Your answer</em>}
                    {chosen && !correct && <em>✕ Your answer</em>}
                  </div>
                );
              })}
            </div>

            {(!current.options || current.options.length === 0) && <div className="solution-no-options">Answer review is unavailable for this question.</div>}

            <div className="solution-explanation">
              <strong>Solution & Explanation</strong>
              <p>{current.isCorrect ? "You selected the correct answer." : answered(current.userAnswer) ? "Your selected answer is incorrect. The correct option is highlighted in green." : "This question was not attempted. The correct option is highlighted in green."}</p>
            </div>
          </section>

          <aside className="solution-palette">
            <div className="solution-palette-head">
              <strong>{activeSection || "Questions"}</strong>
              <span>{safeIndex + 1} / {activeQuestions.length}</span>
            </div>

            <div className="solution-palette-grid">
              {activeQuestions.map((item: any, index: number) => (
                <button key={item.questionId || index} className={`${status(item)} ${index === safeIndex ? "current" : ""}`} onClick={() => setSectionIndex(index)}>
                  {index + 1}
                </button>
              ))}
            </div>

            <div className="solution-legend">
              <span><i className="correct" />Correct</span>
              <span><i className="wrong" />Incorrect</span>
              <span><i className="empty" />Unattempted</span>
            </div>
          </aside>
        </main>

        <footer className="solution-paper-footer">
          <button disabled={safeIndex === 0} onClick={() => setSectionIndex((idx) => Math.max(0, idx - 1))}>
            ← Previous
          </button>
          <button className="solution-back-report" onClick={onClose}>
            Back to report
          </button>
          <button disabled={safeIndex === activeQuestions.length - 1} onClick={() => setSectionIndex((idx) => Math.min(activeQuestions.length - 1, idx + 1))}>
            Next →
          </button>
        </footer>
      </div>
    </div>
  );
};
const QuestionReport=({total,correct,review}:any)=>{
  const [filter,setFilter]=useState("all");

  const allRows = Array.from({ length: total }, (_, i) => ({
    item: review?.[i],
    index: i,
    status: reviewStatus(review?.[i], i, correct, total - correct),
  }));

  const correctCount = allRows.filter((r) => r.status === "correct").length;
  const wrongCount = allRows.filter((r) => r.status === "wrong").length;
  const unattemptedCount = allRows.filter((r) => r.status === "unattempted").length;
  const fasterCount = allRows.filter(
    (r) => r.item?.topperTimeSec > 0 && r.item?.timeSpentSec > 0 && r.item.timeSpentSec <= r.item.topperTimeSec
  ).length;

  const rows = allRows.filter(({ item, status }: any) => {
    if (filter === "all") return true;
    if (filter === "correct") return status === "correct";
    if (filter === "wrong") return status === "wrong";
    if (filter === "unattempted") return status === "unattempted";
    if (filter === "faster") return item?.topperTimeSec > 0 && item?.timeSpentSec > 0 && item?.timeSpentSec <= item?.topperTimeSec;
    if (filter === "slower") return item?.topperTimeSec > 0 && item?.timeSpentSec > item?.topperTimeSec;
    if (filter === "untimed") return !item?.timeSpentSec;
    return true;
  });

  const time = (seconds?: number) =>
    seconds ? `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}` : "—";

  return (
    <div className="report-card table-card question-time-report">
      {/* Header */}
      <div className="question-report-heading">
        <div className="question-report-title-wrap">
          <h3>Question Report</h3>
          <p>Actual time spent compared with the cohort and top performer.</p>
        </div>
      </div>

      {/* Quick Summary Strip */}
      <div className="question-summary-strip">
        <div className="summary-pill total">
          <span className="pill-num">{total}</span>
          <span className="pill-lbl">Total Questions</span>
        </div>
        <div className="summary-pill correct">
          <span className="pill-num">{correctCount}</span>
          <span className="pill-lbl">Correct</span>
        </div>
        <div className="summary-pill wrong">
          <span className="pill-num">{wrongCount}</span>
          <span className="pill-lbl">Incorrect</span>
        </div>
        <div className="summary-pill unattempted">
          <span className="pill-num">{unattemptedCount}</span>
          <span className="pill-lbl">Unattempted</span>
        </div>
        <div className="summary-pill faster">
          <span className="pill-num">{fasterCount}</span>
          <span className="pill-lbl">⚡ Faster Than Topper</span>
        </div>
      </div>

      {/* Quick Filter Pills */}
      <div className="question-filter-pills-row">
        {[
          { id: "all", label: `All (${total})` },
          { id: "correct", label: `✓ Correct (${correctCount})` },
          { id: "wrong", label: `× Incorrect (${wrongCount})` },
          { id: "unattempted", label: `— Unattempted (${unattemptedCount})` },
          { id: "faster", label: `⚡ Faster (${fasterCount})` },
          { id: "slower", label: `🐢 Slower` },
        ].map((btn) => (
          <button
            key={btn.id}
            type="button"
            className={`question-filter-btn ${filter === btn.id ? "active" : ""}`}
            onClick={() => setFilter(btn.id)}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Table Content or Centered Empty State */}
      {rows.length === 0 ? (
        <div className="question-report-empty">
          <div className="qr-empty-icon">🔍</div>
          <h4>No Questions Match This Filter</h4>
          <p>You have 0 questions in this category.</p>
          <button type="button" className="btn-reset-qr-filter" onClick={() => setFilter("all")}>
            View All Questions ({total})
          </button>
        </div>
      ) : (
        <div className="question-table-scroll-wrap">
          <div className="report-table question-table">
            <div className="table-head">
              <span className="col-sno">S.No.</span>
              <span className="col-status">Status</span>
              <span className="col-score">Score</span>
              <span className="col-yourtime">Your Time</span>
              <span className="col-avg">Average</span>
              <span className="col-topper">Topper Time</span>
              <span className="col-compare">Comparison</span>
            </div>

            {rows.map(({ item, index, status }: any) => {
              const delta =
                item?.topperTimeSec && item?.timeSpentSec ? item.timeSpentSec - item.topperTimeSec : 0;
              return (
                <div className="table-row" key={index}>
                  <span className="col-sno">
                    <span className="sno-circle">{index + 1}</span>
                  </span>
                  <span className="col-status">
                    {status === "correct" ? (
                      <span className="qr-status-badge correct">✓ Correct</span>
                    ) : status === "wrong" ? (
                      <span className="qr-status-badge wrong">× Incorrect</span>
                    ) : (
                      <span className="qr-status-badge unattempted">— Unattempted</span>
                    )}
                  </span>
                  <span className="col-score">
                    <span className={`qr-score-badge ${status === "correct" ? "positive" : status === "wrong" ? "negative" : "zero"}`}>
                      {item?.marks ?? (status === "correct" ? "+1.00" : status === "wrong" ? "-0.25" : "0")}
                    </span>
                  </span>
                  <span className="col-yourtime">
                    <b>{time(item?.timeSpentSec)}</b>
                  </span>
                  <span className="col-avg">{time(item?.avgTimeSec)}</span>
                  <span className="col-topper">{time(item?.topperTimeSec)}</span>
                  <span className="col-compare">
                    {!item?.timeSpentSec || !item?.topperTimeSec ? (
                      <span className="qr-delta-chip neutral">—</span>
                    ) : delta <= 0 ? (
                      <span className="qr-delta-chip faster">▲ {Math.abs(delta)}s faster</span>
                    ) : (
                      <span className="qr-delta-chip slower">▼ {delta}s slower</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
const CompareReport=({score,total,pct}:any)=><><div className="compare-grid"><article className="report-card"><h3>Score Comparison</h3><div className="comparison-bars"><Metric label="You" value={score} max={total} color="#2f6fed"/><Metric label="Average" value={total*.52} max={total} color="#29b6d8"/><Metric label="Topper" value={total} max={total} color="#27b487"/></div></article><article className="report-card"><h3>Accuracy Comparison</h3><div className="comparison-bars"><Metric label="You" value={pct} max={100} color="#2f6fed"/><Metric label="Average" value={53} max={100} color="#29b6d8"/><Metric label="Topper" value={98} max={100} color="#27b487"/></div></article></div><div className="report-card topper-card"><h3>Top Performers</h3><div className="topper-bars">{[100,91,86,82,78,72,68,63,58,pct].map((x,i)=><div key={i}><span style={{height:`${Math.max(12,x)}%`}} className={i===9?'you':''}/><small>{i===9?'YOU':`${i+1}${i===0?'st':i===1?'nd':i===2?'rd':'th'}`}</small></div>)}</div></div></>;

export default AnswererDashboard;
